import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { FaMagic, FaImage, FaVideo, FaChevronDown, FaChevronUp, FaSlidersH, FaTimes, FaSpinner } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { calculateModelCost } from '../utils/modelSchemaLoader';

// Shared imports
import ReferenceImageModal from "./pills/ReferenceImageModal";
import DynamicModelForm from "./DynamicModelForm";
// import OutputSamplesModal from "./pills/OutputSamplesModal";
import AIModelSelector from "./pills/AIModelSelector";

const TacoStudioEditor = forwardRef(({
  // Content type - NEW PROP
  contentType, // "image" | "video"
  handleContentTypeChange, // Handler for content type changes
  
  // State values
  artFormat,
  setArtFormat,
  message,
  setMessage,
  messageError,
  textboxUploadedImages,
  setTextboxUploadedImages,
  textboxUploadError,
  // isReferenceLoading,
  selectedModel,
  setSelectedModel,
  // outputSamples,
  isGeneratingContent,
  contentGenerated,
  
  
  // Video-specific props
  isImageUploading,
  
  // Workflow completion state
  isWorkflowComplete,
  
  // Model parameters for DynamicModelForm
  modelParams,
  setModelParams,
  modelFileObjects,
  setModelFileObjects,
  
  // Event handlers
  handleTextboxFileUpload,
  removeTextboxImage,
  handleStopGeneration,
  handleGenerateContent,
  
  // Refs
  messageTextareaRef,
  textboxFileInputRef,
  
}, ref) => {
  // Chat UI state
  const [chatInput, setChatInput] = useState('');
  const chatInputRef = useRef(null);
  const dynamicModelFormRef = useRef(null);
  const [currentSchema, setCurrentSchema] = useState(null);
  const [estimatedCost, setEstimatedCost] = useState(0);

  // Handle schema loaded from DynamicModelForm
  const handleSchemaLoaded = (schema) => {
    setCurrentSchema(schema);
  };

  // Calculate estimated cost
  useEffect(() => {
    if (currentSchema && modelParams) {
      // For free models, cost is 0. Though schema shouldn't be loaded for free models.
      if (selectedModel === 'free' || selectedModel === 'auto') {
        setEstimatedCost(0);
      } else {
        const cost = calculateModelCost(currentSchema, modelParams, artFormat);
        setEstimatedCost(cost);
      }
    } else {
      setEstimatedCost(0);
    }
  }, [currentSchema, modelParams, artFormat, selectedModel]);

  // Collapsible state for optional parameters - persisted in sessionStorage
  const [isOptionalParamsExpanded, setIsOptionalParamsExpanded] = useState(() => {
    try {
      const stored = sessionStorage.getItem('tacoStudio_optionalParamsExpanded');
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  // Persist collapsed state
  useEffect(() => {
    try {
      sessionStorage.setItem('tacoStudio_optionalParamsExpanded', JSON.stringify(isOptionalParamsExpanded));
    } catch (e) {
      // Ignore storage errors
    }
  }, [isOptionalParamsExpanded]);

  // Keep chat input in sync with persisted message
  useEffect(() => {
    setChatInput(message || '');
  }, [message]);

  // Pills modal state
  const [isRefModalOpen, setIsRefModalOpen] = useState(false);
  const [isSamplesModalOpen, setIsSamplesModalOpen] = useState(false);
  
  // Mobile settings modal state
  const [isMobileSettingsOpen, setIsMobileSettingsOpen] = useState(false);
  
  // Mobile bottom bar expansion state - persisted
  const [isMobileBarExpanded, setIsMobileBarExpanded] = useState(() => {
    try {
      const stored = sessionStorage.getItem('tacoStudio_mobileBarExpanded');
      return stored ? JSON.parse(stored) : true; // Default to expanded for first-time users
    } catch {
      return true;
    }
  });
  
  // Persist mobile bar expansion state
  useEffect(() => {
    try {
      sessionStorage.setItem('tacoStudio_mobileBarExpanded', JSON.stringify(isMobileBarExpanded));
    } catch (e) {
      // Ignore storage errors
    }
  }, [isMobileBarExpanded]);

  // Computed values
  const isFreeModel = selectedModel === 'free';
  const hasMask = !!textboxUploadedImages[0]?.maskBlob;

  // Send from chatbox
  const handleSendChat = () => {
    // Collapse mobile bar smoothly when Generate is clicked
    if (isMobileBarExpanded) {
      setIsMobileBarExpanded(false);
    }
    handleValidatedGenerateContent();
  };

  // Handle validation before calling parent's handleGenerateContent
  const handleValidatedGenerateContent = () => {
    // Resolve final message
    const trimmedInput = (chatInput || '').trim();

    if (trimmedInput) {
      setMessage(trimmedInput);
    } else {
      setMessage('');
    }

    // Proceed with generation
    setTimeout(() => {
      handleGenerateContent();
    }, 0);
  };

  // Set art format based on content type
  useEffect(() => {
    const desiredPostType = contentType === "video" ? 'video' : 'image';
    setArtFormat(desiredPostType);
  }, [setArtFormat, contentType]);

  // Reset model parameters when model changes
  useEffect(() => {
    if (selectedModel && setModelParams) {
      // Reset parameters when model changes (but keep if same model)
      // This allows the form to reload defaults for the new model
      setModelParams({});
      setCurrentSchema(null); // Reset schema
    }
    if (selectedModel && setModelFileObjects) {
      setModelFileObjects({});
    }
  }, [selectedModel, setModelParams, setModelFileObjects]);

  // Handle model parameter changes from DynamicModelForm
  const handleModelParamsChange = (values, fileObjects) => {
    if (setModelParams) {
      setModelParams(values);
    }
    if (setModelFileObjects && fileObjects) {
      setModelFileObjects(fileObjects);
    }
  };

  // Helper to check if model has mandatory parameters
  const hasMandatoryParams = () => {
    // Only models with schemas (not 'free' or 'auto') might have params
    return selectedModel && selectedModel !== 'free' && selectedModel !== 'auto';
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    /**
     * Validates the DynamicModelForm if a model with schema is selected
     * @returns {Object} { isValid: boolean, errors: Object }
     */
    validateModelForm: () => {
      if (dynamicModelFormRef.current && selectedModel && selectedModel !== 'free' && selectedModel !== 'auto') {
        return dynamicModelFormRef.current.validate();
      }
      // No validation needed for free/auto models
      return { isValid: true, errors: {} };
    }
  }), [selectedModel]);

  // Upscale icon component
  const UpscaleIcon = ({ size = "base" }) => {
    const iconSize = size === "sm" ? "14" : "16";
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
        <path d="M13 15c-2 0 -5 1 -5 5" />
        <path d="M4 11m0 2a2 2 0 0 1 2 -2h5a2 2 0 0 1 2 2v5a2 2 0 0 1 -2 2h-5a2 2 0 0 1 -2 -2z" />
        <path d="M4 7v-2a1 1 0 0 1 1 -1h2" />
        <path d="M11 4h2" />
        <path d="M17 4h2a1 1 0 0 1 1 1v2" />
        <path d="M20 11v2" />
        <path d="M20 17v2a1 1 0 0 1 -1 1h-2" />
      </svg>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Mobile View: Expandable Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#171717] border-t border-white/10 shadow-2xl">
        {/* Content Type Tabs - Positioned dynamically just above AI Model Selector */}
        {handleContentTypeChange && (
          <div className="bg-[#0f0f0f] border-b border-white/10">
            <div className="flex items-end px-4 pt-2 gap-0">
              {/* Image Tab */}
              <button
                onClick={() => handleContentTypeChange("image")}
                className={`relative flex items-center justify-center gap-1 px-2.5 py-1.5 text-[11px] font-medium transition-all duration-200 ${
                  contentType === "image"
                    ? "bg-[#171717] text-white z-10"
                    : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
                }`}
                style={{
                  borderRadius: contentType === "image" ? "8px 8px 0 0" : "8px 0 0 0",
                  marginRight: contentType === "image" ? "-1px" : "0"
                }}
              >
                <FaImage className="text-[16px]" />
                <span>Image</span>
              </button>
              {/* Video Tab */}
              <button
                onClick={() => handleContentTypeChange("video")}
                className={`relative flex items-center justify-center gap-1 px-2.5 py-1.5 text-[11px] font-medium transition-all duration-200 ${
                  contentType === "video"
                    ? "bg-[#171717] text-white z-10"
                    : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
                }`}
                style={{
                  borderRadius: contentType === "video" ? "8px 8px 0 0" : "0",
                  marginLeft: contentType === "video" ? "-1px" : "0",
                  marginRight: contentType === "video" ? "-1px" : "0"
                }}
              >
                <FaVideo className="text-[16px]" />
                <span>Video</span>
              </button>
              
              {/* Upscale Tab */}
              <button
                onClick={() => handleContentTypeChange("upscale")}
                className={`relative flex items-center justify-center gap-1 px-2.5 py-1.5 text-[11px] font-medium transition-all duration-200 ${
                  contentType === "upscale"
                    ? "bg-[#171717] text-white z-10"
                    : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
                }`}
                style={{
                  borderRadius: contentType === "upscale" ? "8px 8px 0 0" : "0 8px 0 0",
                  marginLeft: contentType === "upscale" ? "-1px" : "0"
                }}
              >
                <UpscaleIcon size="base" />
                <span>Upscale</span>
              </button>
            </div>
          </div>
        )}

        {/* Expandable Content Section */}
        <AnimatePresence initial={false}>
          {isMobileBarExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              className="overflow-hidden border-b border-white/5"
            >
              <div className="px-3 py-2 space-y-2.5 max-h-[50vh] overflow-y-auto">
                {/* AI Model Selector */}
                <div className="flex items-center gap-2">
                  {selectedModel && (
                    <div className="flex-1 min-w-0">
                      <AIModelSelector
                        selectedModelId={selectedModel || 'auto'}
                        contentType={contentType}
                        artFormat={artFormat}
                        onModelSelect={(modelId) => {
                          if (setSelectedModel) setSelectedModel(modelId);
                          if (setModelParams) setModelParams({});
                          if (setModelFileObjects) setModelFileObjects({});
                        }}
                      />
                    </div>
                  )}
                </div>
                
                {/* Mandatory Parameters - Compact */}
                {hasMandatoryParams() && (
                  <div>
                    <div className="bg-[#0f0f0f]/50 rounded-lg p-2 border border-white/5">
                      <DynamicModelForm
                        ref={dynamicModelFormRef}
                        modelId={selectedModel}
                        values={modelParams || {}}
                        onChange={handleModelParamsChange}
                        onSchemaLoaded={handleSchemaLoaded}
                        showLabels={true}
                        showOnlyRequired={true}
                        isOptionalParamsExpanded={false}
                        onToggleOptionalParams={null}
                      />
                    </div>
                    <div className="flex items-center justify-end mb-1">
                      <button
                        onClick={() => setIsMobileSettingsOpen(true)}
                        className="text-[10px] text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1"
                      >
                        <FaSlidersH className="text-[9px]" />
                        <span>Model Settings</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Always Visible Action Bar */}
        <div className="p-2 flex items-center gap-2">
          {/* Expand/Collapse Toggle */}
          <button
            type="button"
            onClick={() => setIsMobileBarExpanded(!isMobileBarExpanded)}
            className={`shrink-0 p-2.5 rounded-lg border transition-all duration-300 ${
              isMobileBarExpanded
                ? 'bg-[#0f0f0f] border-white/10 hover:bg-white/5 text-gray-300 hover:text-white'
                : 'bg-blue-500/20 border-blue-400/50 hover:bg-blue-500/30 text-blue-300 hover:text-blue-200 shadow-lg shadow-blue-500/20 animate-pulse'
            }`}
            title={isMobileBarExpanded ? "Hide settings" : "Show settings"}
          >
            {isMobileBarExpanded ? (
              <FaChevronDown className="text-sm" />
            ) : (
              <FaChevronUp className="text-sm" />
            )}
          </button>
          
          {/* Quick Model Display (when collapsed) */}
          {!isMobileBarExpanded && selectedModel && (
            <div className="flex-1 min-w-0 text-[11px] text-gray-400 truncate px-2">
              {selectedModel}
            </div>
          )}
          
          {/* Settings Icon - For optional parameters */}
          {!isMobileBarExpanded && (
            <button
              type="button"
              onClick={() => setIsMobileSettingsOpen(true)}
              className="shrink-0 p-2.5 rounded-lg bg-[#0f0f0f] border border-white/10 hover:bg-white/5 text-gray-300 hover:text-white transition-all duration-200"
              title="Advanced Settings"
            >
              <FaSlidersH className="text-sm" />
            </button>
          )}
          
          {/* Generate Button */}
          <div className="flex flex-col items-end gap-1">
            {isGeneratingContent && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="text-[9px] text-gray-400 font-medium whitespace-nowrap"
              >
                One at a time
              </motion.div>
            )}
            <button
              type="button"
              disabled={isGeneratingContent}
              onClick={handleSendChat}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-xs font-semibold transition-all duration-300 shadow-lg ${
                isGeneratingContent 
                  ? 'bg-gray-600 text-white/80 cursor-not-allowed animate-pulse' 
                  : isWorkflowComplete 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-green-500/30' 
                    : 'bg-white text-black hover:opacity-90'
              }`}
            >
              {isGeneratingContent ? (
                <FaSpinner className="text-xs animate-spin" />
              ) : (
                <FaMagic className={`text-xs ${isWorkflowComplete ? 'animate-bounce' : ''}`} />
              )}
              <span className="flex flex-col items-start">
                <span>
                  {isGeneratingContent ? 'Generating...' : 'Generate'}
                </span>
                {!isGeneratingContent && estimatedCost > 0 && (
                  <span className="text-[9px] font-normal opacity-75 leading-tight">
                    ₹{estimatedCost.toFixed(2)} incl. GST
                  </span>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Settings Modal - Full Screen */}
      <AnimatePresence>
        {isMobileSettingsOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm"
            onClick={() => setIsMobileSettingsOpen(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="absolute inset-x-0 bottom-0 bg-[#171717] rounded-t-2xl max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex-shrink-0 px-4 py-3 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaSlidersH className="text-gray-400" />
                  <h2 className="text-sm font-semibold text-white">Settings & Parameters</h2>
                </div>
                <button
                  onClick={() => setIsMobileSettingsOpen(false)}
                  className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
                >
                  <FaTimes className="text-sm" />
                </button>
              </div>
              
              {/* Modal Content - Scrollable */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                {/* AI Model Selector */}
                {selectedModel && (
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">
                      AI Model
                    </label>
                    <AIModelSelector
                      selectedModelId={selectedModel || 'auto'}
                      contentType={contentType}
                      artFormat={artFormat}
                      onModelSelect={(modelId) => {
                        if (setSelectedModel) setSelectedModel(modelId);
                        if (setModelParams) setModelParams({});
                        if (setModelFileObjects) setModelFileObjects({});
                      }}
                    />
                  </div>
                )}
                
                {/* Model Parameters */}
                {selectedModel && selectedModel !== 'free' && selectedModel !== 'auto' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">
                      Parameters
                    </label>
                    <div className="bg-[#0f0f0f] rounded-lg p-3 border border-white/5">
                      <DynamicModelForm
                        ref={dynamicModelFormRef}
                        modelId={selectedModel}
                        values={modelParams || {}}
                        onChange={handleModelParamsChange}
                        onSchemaLoaded={handleSchemaLoaded}
                        showLabels={true}
                        isOptionalParamsExpanded={isOptionalParamsExpanded}
                        onToggleOptionalParams={() => setIsOptionalParamsExpanded(!isOptionalParamsExpanded)}
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Modal Footer with Generate Button */}
              <div className="flex-shrink-0 px-4 py-3 border-t border-white/10 bg-[#0f0f0f]/50">
                <div className="flex flex-col items-center gap-1.5">
                  {/* {isGeneratingContent && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="text-[10px] text-gray-400 font-medium"
                    >
                      One generation at a time
                    </motion.div>
                  )} */}
                  <button
                    type="button"
                    disabled={isGeneratingContent}
                    onClick={() => {
                      setIsMobileSettingsOpen(false);
                      handleSendChat();
                    }}
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-lg py-3 text-sm font-semibold transition-all duration-300 ${
                      isGeneratingContent 
                        ? 'bg-gray-600 text-white/80 cursor-not-allowed animate-pulse' 
                        : isWorkflowComplete 
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 shadow-lg shadow-green-500/25' 
                          : 'bg-white text-black hover:opacity-90 shadow-lg'
                    }`}
                    title={isWorkflowComplete ? "All steps completed! Ready to generate" : "Generate"}
                  >
                    {isGeneratingContent ? (
                      <FaSpinner className="text-sm animate-spin" />
                    ) : (
                      <FaMagic className={`text-sm ${isWorkflowComplete ? 'animate-bounce' : ''}`} />
                    )}
                    <span className="flex flex-col items-center">
                      <span>
                        {isGeneratingContent ? 'Generating...' : 'Generate Content'}
                      </span>
                      {!isGeneratingContent && estimatedCost > 0 && (
                        <span className="text-[10px] font-normal opacity-75 leading-tight">
                          ₹{estimatedCost.toFixed(2)} incl. GST
                        </span>
                      )}
                    </span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content Type Tabs - Desktop View (Chrome-style with rounded edges) */}
      {handleContentTypeChange && (
        <div className="hidden lg:flex flex-shrink-0 bg-[#0f0f0f] border-b border-white/10">
          <div className="flex items-end px-2 pt-2 gap-0">
            {/* Image Tab */}
            <button
              onClick={() => handleContentTypeChange("image")}
              className={`relative flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium transition-all duration-200 ${
                contentType === "image"
                  ? "bg-[#171717] text-white z-10"
                  : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
              }`}
              style={{
                borderRadius: contentType === "image" ? "10px 10px 0 0" : "10px 0 0 0",
                marginRight: contentType === "image" ? "-2px" : "0"
              }}
            >
              <FaImage className="text-base" />
              <span>Image</span>
            </button>
            
            {/* Video Tab */}
            <button
              onClick={() => handleContentTypeChange("video")}
              className={`relative flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium transition-all duration-200 ${
                contentType === "video"
                  ? "bg-[#171717] text-white z-10"
                  : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
              }`}
              style={{
                borderRadius: contentType === "video" ? "10px 10px 0 0" : "0",
                marginLeft: contentType === "video" ? "-2px" : "0",
                marginRight: contentType === "video" ? "-2px" : "0"
              }}
            >
              <FaVideo className="text-base" />
              <span>Video</span>
            </button>
            
            {/* Upscale Tab */}
            <button
              onClick={() => handleContentTypeChange("upscale")}
              className={`relative flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium transition-all duration-200 ${
                contentType === "upscale"
                  ? "bg-[#171717] text-white z-10"
                  : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
              }`}
              style={{
                borderRadius: contentType === "upscale" ? "10px 10px 0 0" : "0 10px 0 0",
                marginLeft: contentType === "upscale" ? "-2px" : "0"
              }}
            >
              <UpscaleIcon />
              <span>Upscale</span>
            </button>
          </div>
        </div>
      )}

      {/* Desktop View: Right Panel */}
      <div className="hidden lg:flex flex-col h-full bg-[#171717] border-l border-white/5 overflow-hidden">
        {/* Header - Sticky at top */}
        <div className="flex-shrink-0 px-3 py-3 border-b border-white/5 bg-[#171717] sticky top-0 z-10">
          {/* AI Model Selector */}
          {selectedModel && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">
                AI Model
              </label>
              <AIModelSelector
                selectedModelId={selectedModel || 'auto'}
                contentType={contentType}
                artFormat={artFormat}
                onModelSelect={(modelId) => {
                  if (setSelectedModel) setSelectedModel(modelId);
                  if (setModelParams) setModelParams({});
                  if (setModelFileObjects) setModelFileObjects({});
                }}
              />
            </div>
          )}
        </div>

        {/* Parameters section - Scrollable to allow footer to stay visible */}
        <div className="flex-1 px-3 py-3 overflow-y-auto min-h-0">
          {selectedModel && selectedModel !== 'free' && selectedModel !== 'auto' && (
            <div className="h-full flex flex-col">
              <div className="pb-2 border-b border-white/5 flex-shrink-0">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Parameters</h3>
              </div>
              <div className="flex-1 min-h-0 mt-3">
                <DynamicModelForm
                  ref={dynamicModelFormRef}
                  modelId={selectedModel}
                  values={modelParams || {}}
                  onChange={handleModelParamsChange}
                  onSchemaLoaded={handleSchemaLoaded}
                  className="h-full"
                  showLabels={true}
                  isOptionalParamsExpanded={isOptionalParamsExpanded}
                  onToggleOptionalParams={() => setIsOptionalParamsExpanded(!isOptionalParamsExpanded)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer - Sticky at bottom */}
        <div className="flex-shrink-0 px-3 py-3 border-t border-white/5 bg-[#171717] sticky bottom-0 z-[5]">
          <div className="flex flex-col items-center gap-1.5">
            {/* {isGeneratingContent && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="text-[10px] text-gray-400 font-medium"
              >
                One generation at a time
              </motion.div>
            )} */}
            <button
              type="button"
              disabled={isGeneratingContent}
              onClick={handleSendChat}
              data-tour="generate-button"
              className={`w-full inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-semibold transition-all duration-300 ${
                isGeneratingContent 
                  ? 'bg-gray-600 text-white/80 cursor-not-allowed animate-pulse' 
                  : isWorkflowComplete 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 shadow-lg shadow-green-500/25' 
                    : 'bg-white text-black hover:opacity-90'
              }`}
              title={isWorkflowComplete ? "All steps completed! Ready to generate" : "Generate"}
            >
              {isGeneratingContent ? (
                <FaSpinner className="text-xs animate-spin" />
              ) : (
                <FaMagic className={`text-xs ${isWorkflowComplete ? 'animate-bounce' : ''}`} />
              )}
              <span className="flex flex-col items-center">
                <span>
                  {isGeneratingContent ? 'Generating...' : 'Generate'}
                </span>
                {!isGeneratingContent && estimatedCost > 0 && (
                  <span className="text-[9px] font-normal opacity-75 leading-tight">
                    ₹{estimatedCost.toFixed(2)} incl. GST
                  </span>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Setting modals (remain global) */}
      {/* {isRefModalOpen && (
        <ReferenceImageModal
          isOpen={isRefModalOpen}
          onClose={() => setIsRefModalOpen(false)}
          textboxUploadedImages={textboxUploadedImages}
          textboxUploadError={textboxUploadError}
          handleTextboxFileUpload={handleTextboxFileUpload}
          removeTextboxImage={removeTextboxImage}
          textboxFileInputRef={textboxFileInputRef}
          setTextboxUploadedImages={setTextboxUploadedImages}
          disableMasking={contentType === "video"}
          artFormat={artFormat}
        />
      )} */}
    </div>
  );
});

// Add display name for debugging
TacoStudioEditor.displayName = 'TacoStudioEditor';

export default TacoStudioEditor;

